import { React, useEffect, useState } from "react";
import CustomInput from "../components/CustomInput";
import ReactQuill from "react-quill";
import { useLocation, useNavigate } from "react-router-dom";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { getBrands } from "../features/brand/brandSlice";
import { getCategories } from "../features/pcategory/pcategorySlice";
import { getColors } from "../features/color/colorSlice";
import { Select } from "antd";
import Dropzone from "react-dropzone";
import { delImg, uploadImg } from "../features/upload/uploadSlice";
import {
  createProducts,
  getAProduct,
  resetState,
  updateAProduct,
} from "../features/product/productSlice";
let schema = yup.object().shape({
  title: yup.string().required("Title is Required"),
  description: yup.string().required("Description is Required"),
  price: yup.number().required("Price is Required").typeError("Price must be a number"),
  originalPrice: yup.number().nullable().typeError("Original Price must be a number"),
  discountedPrice: yup.number().nullable().typeError("Discounted Price must be a number"),
  brand: yup.string().required("Brand is Required"),
  category: yup.string().required("Category is Required"),
  tags: yup.string().required("Tag is Required"),
  quantity: yup.number().required("Quantity is Required").typeError("Quantity must be a number"),
});

const Addproduct = ({ editId, editMode, onSuccess }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const getProductId = editId || location.pathname.split("/")[3];
  const isInModal = location.state?.fromModal;
  const [images, setImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    dispatch(getBrands());
    dispatch(getCategories());
    dispatch(getColors());
  }, []);

  const brandState = useSelector((state) => state.brand.brands);
  const catState = useSelector((state) => state.pCategory.pCategories);
  const imgState = useSelector((state) => state?.upload?.images);
  const newProduct = useSelector((state) => state.product);
  const {
    isSuccess,
    isError,
    isLoading,
    createdProduct,
    updatedProduct,
    productName,
    productDesc,
    productPrice,
    productOriginalPrice,
    productDiscountedPrice,
    productBrand,
    productCategory,
    productTag,
    productQuantity,
    productImages,
    productIsPreorder,
    productPreorderReleaseDate,
    productPreorderDeposit,
    productPreorderNotes,
  } = newProduct;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: getProductId && productName ? productName : "",
      description: getProductId && productDesc ? productDesc : "",
      price: getProductId && productPrice ? productPrice : "",
      originalPrice: getProductId && newProduct.productOriginalPrice ? newProduct.productOriginalPrice : "",
      discountedPrice: getProductId && newProduct.productDiscountedPrice ? newProduct.productDiscountedPrice : "",
      brand: getProductId && productBrand ? productBrand : "",
      category: getProductId && productCategory ? productCategory : "",
      tags: getProductId && productTag ? productTag : "",
      quantity: getProductId && productQuantity ? productQuantity : "",
      images: getProductId && productImages ? productImages : [],
      // Preorder fields
      isPreorder: getProductId && productIsPreorder !== undefined ? productIsPreorder : false,
      preorderReleaseDate: getProductId && productPreorderReleaseDate ? productPreorderReleaseDate : "",
      preorderDeposit: getProductId && productPreorderDeposit ? productPreorderDeposit : 0,
      preorderNotes: getProductId && productPreorderNotes ? productPreorderNotes : "",
    },
    validationSchema: schema,
    onSubmit: (values) => {
      // Ensure numeric values are properly converted to numbers
      const numericValues = {
        ...values,
        price: values.price !== "" ? parseFloat(values.price) : 0,
        originalPrice: values.originalPrice !== "" ? parseFloat(values.originalPrice) : null,
        discountedPrice: values.discountedPrice !== "" ? parseFloat(values.discountedPrice) : null,
        quantity: values.quantity !== "" ? parseInt(values.quantity, 10) : 0,
        preorderDeposit: values.preorderDeposit !== "" ? parseFloat(values.preorderDeposit) : 0
      };
      
      console.log(numericValues);
      setIsSubmitting(true);
      if (getProductId !== undefined) {
        const data = { id: getProductId, productData: numericValues };
        dispatch(updateAProduct(data));
      } else {
        dispatch(createProducts(numericValues));
        formik.resetForm();
        setImages([]);
        setTimeout(() => {
          dispatch(resetState());
        }, 3000);
      }
    },
  });

  // Reset form when switching between add and edit modes
  useEffect(() => {
    if (getProductId !== undefined) {
      // Editing mode - fetch product data
      dispatch(getAProduct(getProductId));
    } else {
      // Adding mode - reset form
      dispatch(resetState());
      setImages([]);
      if (formik) {
        formik.resetForm();
      }
    }
  }, [getProductId, editMode, editId]);

  useEffect(() => {
    if (isSuccess && createdProduct) {
      toast.success("Product Added Successfullly!");
      setIsSubmitting(false);
      // Reset state to prevent multiple calls
      dispatch(resetState());
      if (onSuccess) {
        onSuccess();
      }
      if (!isInModal) {
        navigate("/admin/catalog");
      }
    }
    if (isSuccess && updatedProduct) {
      toast.success("Product Updated Successfullly!");
      setIsSubmitting(false);
      // Reset state to prevent multiple calls
      dispatch(resetState());
      if (onSuccess) {
        onSuccess();
      }
      if (!isInModal) {
        navigate("/admin/catalog");
      }
    }
    if (isError) {
      toast.error("Something Went Wrong!");
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, isLoading, createdProduct, updatedProduct, onSuccess, dispatch]);

  // Process images from imgState (uploaded images)
  useEffect(() => {
    if (imgState && imgState.length > 0) {
      setImages(prevImages => {
        // Filter out duplicates and add new images
        const newImages = imgState.filter(
          newImg => !prevImages.some(prevImg => prevImg.public_id === newImg.public_id)
        );
        return [...prevImages, ...newImages];
      });
      setIsUploading(false);
    }
  }, [imgState]);

  // Process existing product images (for edit mode)
  useEffect(() => {
    if (getProductId && productImages && productImages.length > 0) {
      setImages(productImages);
    } else if (!getProductId) {
      // Clear images when switching to add mode
      setImages([]);
    }
  }, [productImages, getProductId, editMode, editId]);

  // Update formik values when images change
  useEffect(() => {
    if (formik) {
      formik.setFieldValue("images", images);
    }
  }, [images]);

  // Update form values when product data is loaded for editing
  useEffect(() => {
    if (getProductId && formik) {
      formik.setValues({
        title: productName || "",
        description: productDesc || "",
        price: productPrice || "",
        originalPrice: productOriginalPrice || "",
        discountedPrice: productDiscountedPrice || "",
        brand: productBrand || "",
        category: productCategory || "",
        tags: productTag || "",
        quantity: productQuantity || "",
        images: productImages || [],
        // Preorder fields
        isPreorder: productIsPreorder !== undefined ? productIsPreorder : false,
        preorderReleaseDate: productPreorderReleaseDate || "",
        preorderDeposit: productPreorderDeposit || 0,
        preorderNotes: productPreorderNotes || "",
      });
    } else if (!getProductId && formik) {
      // Reset form for add mode
      formik.setValues({
        title: "",
        description: "",
        price: "",
        originalPrice: "",
        discountedPrice: "",
        brand: "",
        category: "",
        tags: "",
        quantity: "",
        images: [],
        // Preorder fields
        isPreorder: false,
        preorderReleaseDate: "",
        preorderDeposit: 0,
        preorderNotes: "",
      });
      setImages([]);
    }
  }, [productName, productDesc, productPrice, productOriginalPrice, productDiscountedPrice, productBrand, productCategory, productTag, productQuantity, productImages, productIsPreorder, productPreorderReleaseDate, productPreorderDeposit, productPreorderNotes, getProductId, editMode, editId]);

  const removeImage = (public_id) => {
    setImages(prevImages => prevImages.filter(img => img.public_id !== public_id));
  };

  const removeUploadedImg = (public_id) => {
    setImages(prevImages => prevImages.filter(img => img.public_id !== public_id));
    dispatch(delImg(public_id));
  };

  return (
    <div>
      <h3 className="mb-4 title">
        {editMode || (getProductId !== undefined) ? "Edit" : "Add"} Product
      </h3>
      <div>
        <form
          onSubmit={formik.handleSubmit}
          className="flex flex-col gap-3 md:gap-4"
        >
          <div className="w-full">
            <CustomInput
              type="text"
              label="Enter Product Title"
              name="title"
              onChng={formik.handleChange("title")}
              onBlr={formik.handleBlur("title")}
              val={formik.values.title}
            />
          </div>
          <div className="error text-sm">
            {formik.touched.title && formik.errors.title}
          </div>
          <div className="w-full">
            <ReactQuill
              theme="snow"
              name="description"
              onChange={formik.handleChange("description")}
              value={formik.values.description}
            />
          </div>
          <div className="error text-sm">
            {formik.touched.description && formik.errors.description}
          </div>
          <div className="w-full">
            <CustomInput
              type="number"
              label="Enter Product Price"
              name="price"
              onChng={formik.handleChange("price")}
              onBlr={formik.handleBlur("price")}
              val={formik.values.price}
            />
          </div>
          <div className="error text-sm">
            {formik.touched.price && formik.errors.price}
          </div>
          
          {/* Original Price Field */}
          <div className="w-full">
            <CustomInput
              type="number"
              label="Enter Original Price (MRP)"
              name="originalPrice"
              onChng={formik.handleChange("originalPrice")}
              onBlr={formik.handleBlur("originalPrice")}
              val={formik.values.originalPrice}
            />
          </div>
          <div className="error text-sm">
            {formik.touched.originalPrice && formik.errors.originalPrice}
          </div>
          
          {/* Discounted Price Field */}
          <div className="w-full">
            <CustomInput
              type="number"
              label="Enter Discounted Price (Sale Price)"
              name="discountedPrice"
              onChng={formik.handleChange("discountedPrice")}
              onBlr={formik.handleBlur("discountedPrice")}
              val={formik.values.discountedPrice}
            />
          </div>
          <div className="error text-sm">
            {formik.touched.discountedPrice && formik.errors.discountedPrice}
          </div>
          
          <div className="w-full">
            <select
              name="brand"
              onChange={formik.handleChange("brand")}
              onBlur={formik.handleBlur("brand")}
              value={formik.values.brand}
              className="form-control py-2 md:py-3 mb-3 w-full"
              id=""
            >
              <option value="">Select Brand</option>
              {brandState.map((i, j) => {
                return (
                  <option key={j} value={i.title}>
                    {i.title}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="error text-sm">
            {formik.touched.brand && formik.errors.brand}
          </div>
          <div className="w-full">
            <select
              name="category"
              onChange={formik.handleChange("category")}
              onBlur={formik.handleBlur("category")}
              value={formik.values.category}
              className="form-control py-2 md:py-3 mb-3 w-full"
              id=""
            >
              <option value="">Select Category</option>
              {catState.map((i, j) => {
                return (
                  <option key={j} value={i.title}>
                    {i.title}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="error text-sm">
            {formik.touched.category && formik.errors.category}
          </div>
          <div className="w-full">
            <select
              name="tags"
              onChange={formik.handleChange("tags")}
              onBlur={formik.handleBlur("tags")}
              value={formik.values.tags}
              className="form-control py-2 md:py-3 mb-3 w-full"
              id=""
            >
              <option value="" disabled>
                Select Category
              </option>
              <option value="featured">Featured</option>
              <option value="popular">Popular</option>
              <option value="special">Special</option>
              <option value="bestseller">Best Seller</option>
              <option value="newarrival">New Arrival</option>
              <option value="comingsoon">Coming Soon</option>
            </select>
          </div>
          <div className="error text-sm">
            {formik.touched.tags && formik.errors.tags}
          </div>


          <div className="w-full">
            <CustomInput
              type="number"
              label="Enter Product Quantity"
              name="quantity"
              onChng={formik.handleChange("quantity")}
              onBlr={formik.handleBlur("quantity")}
              val={formik.values.quantity}
            />
          </div>
          <div className="error text-sm">
            {formik.touched.quantity && formik.errors.quantity}
          </div>
          
          {/* Preorder Section */}
          <div className="border p-3 rounded w-full">
            <h5 className="mb-3 text-lg">Preorder Options</h5>
            
            {/* Preorder Toggle */}
            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="isPreorder"
                name="isPreorder"
                onChange={formik.handleChange("isPreorder")}
                onBlur={formik.handleBlur("isPreorder")}
                checked={formik.values.isPreorder}
              />
              <label className="form-check-label" htmlFor="isPreorder">
                Enable Preorder
              </label>
            </div>
            
            {/* Preorder Fields (only show when preorder is enabled) */}
            {formik.values.isPreorder && (
              <div className="flex flex-col gap-3">
                <div className="w-full">
                  <CustomInput
                    type="date"
                    label="Preorder Release Date"
                    name="preorderReleaseDate"
                    onChng={formik.handleChange("preorderReleaseDate")}
                    onBlr={formik.handleBlur("preorderReleaseDate")}
                    val={formik.values.preorderReleaseDate}
                  />
                </div>
                <div className="w-full">
                  <CustomInput
                    type="number"
                    label="Preorder Deposit Amount (optional)"
                    name="preorderDeposit"
                    onChng={formik.handleChange("preorderDeposit")}
                    onBlr={formik.handleBlur("preorderDeposit")}
                    val={formik.values.preorderDeposit}
                  />
                </div>
                <div className="mb-3 w-full">
                  <label className="form-label block mb-2">Preorder Notes</label>
                  <textarea
                    className="form-control w-full p-2 border rounded"
                    name="preorderNotes"
                    onChange={formik.handleChange("preorderNotes")}
                    onBlur={formik.handleBlur("preorderNotes")}
                    value={formik.values.preorderNotes}
                    rows="3"
                    placeholder="Additional information about the preorder..."
                  />
                </div>
              </div>
            )}
          </div>

          <div className="bg-white border-1 p-4 md:p-5 text-center w-full">
            <Dropzone
              onDrop={(acceptedFiles) => {
                setIsUploading(true);
                dispatch(uploadImg(acceptedFiles));
              }}
            >
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div {...getRootProps()} className="p-4 md:p-6 border-2 border-dashed rounded cursor-pointer relative">
                    <input {...getInputProps()} />
                    {isUploading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      </div>
                    )}
                    <p className="text-sm md:text-base">
                      Drag 'n' drop some files here, or click to select files
                    </p>
                    {isUploading && <p className="text-sm mt-2">Uploading...</p>}
                  </div>
                </section>
              )}
            </Dropzone>
          </div>
          <div className="showimages flex flex-wrap gap-3 w-full">
            {images.map((i, j) => {
              return (
                <div className="relative" key={j}>
                  <button
                    type="button"
                    onClick={() => removeUploadedImg(i.public_id)}
                    className="btn-close absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                  >
                    ×
                  </button>
                  <img src={i.url} alt="" className="w-24 h-24 md:w-32 md:h-32 object-cover rounded" />
                </div>
              );
            })}
          </div>
          <button
            className="btn btn-success border-0 rounded-3 my-5 w-full md:w-auto py-2 px-4 flex items-center justify-center"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {editMode || (getProductId !== undefined) ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>{editMode || (getProductId !== undefined) ? "Edit" : "Add"} Product</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Addproduct;